<script>
import Cookies from 'js-cookie'
import { mapActions } from 'vuex'
export default {
  name: 'TokenMixin',
  methods: {
    ...mapActions(['showAlert']),

    // =============================== User Related ===============================
    _getCookieName() {
      return `${this.$config.VUE_APP_RUN_MODE}_CMS_LID`
    },

    gfGetCurrentUserData() {
      try {
        const data = Cookies.get(this._getCookieName())
        if (this.$validate.DataValid(data)) {
          const userData = JSON.parse(data)
          if (this.$validate.DataValid(userData.id) && userData.id > -1) {
            return userData
          }
        }
      } catch {}

      return {}
    },
    /**
     * @param {Object} data - { id: -1, user_type: '', username: '', schools: [], selected_school: -1, last_login: new Date().getTime() }
     */
    gfSetUserData(data) {
      const userData = this.gfGetCurrentUserData()
      if (this.$validate.DataValid(userData)) {
        Cookies.remove(this._getCookieName())
      }

      const expired = new Date()
      expired.setMonth(expired.getMonth() + 1) // expires after 1 month

      Cookies.set(this._getCookieName(), JSON.stringify(data), {
        path: '/',
        expires: expired,
        sameSite: 'strict',
        secure: window.location.protocol.indexOf('https') > -1 ? true : false,
      })
    },

    gfCheckUserData() {
      let resultType = false
      const userData = this.gfGetCurrentUserData()

      if (this.$validate.DataValid(userData)) {
        if (
          this.$validate.DataValid(userData.id) &&
          this.$validate.DataValid(userData.username) &&
          this.$validate.DataValid(userData.user_type)
        ) {
          resultType = true
        }
      }

      if (!resultType) {
        this.gfForceLogout()
      } else {
        const linkName = this.$route.name
        if (linkName && !this.$validate.allowAction(linkName, userData.user_type)) {
          resultType = false
          this.showAlert({ message: 'noPermission' })
          this.$store.dispatch('toggleLoadingPage', false)
        }
      }

      return resultType
    },

    gfGetUserType() {
      // 'developer' | 'manager' | 'admin' | 'tutor' | 'student'
      const userData = this.gfGetCurrentUserData()
      return userData.user_type || ''
    },
    gfGetUserSchools() {
      const userData = this.gfGetCurrentUserData()
      return userData.schools || []
    },
    gfGetUserSelectedSchool() {
      const userData = this.gfGetCurrentUserData()
      if (
        this.$validate.DataValid(userData.schools) &&
        this.$validate.DataValid(userData.selected_school) &&
        userData.schools.includes(userData.selected_school)
      ) {
        return userData.selected_school
      }

      return -1
    },

    _updateSavedUserData(updateData = {}) {
      const check = this.gfCheckUserData()
      if (check) {
        const userData = this.gfGetCurrentUserData()
        const newData = {
          ...userData,
          ...updateData,
        }

        this.gfSetUserData(newData)

        return true
      }

      return false
    },
    gfUpdateUserSchools(schools) {
      return this._updateSavedUserData({ schools: schools })
    },
    gfUpdateUserSelectedSchool(selectedSchool) {
      return this._updateSavedUserData({ selected_school: selectedSchool })
    },
    gfUpdateUserAccessToken(token) {
      return this._updateSavedUserData({ access_token: token })
    },
    gfUpdateUserType(userType) {
      return this._updateSavedUserData({ user_type: userType })
    },
    gfUpdateUsername(username) {
      return this._updateSavedUserData({ username: username })
    },

    gfRemoveUserCookies() {
      const userData = this.gfGetCurrentUserData()
      if (this.$validate.DataValid(userData)) {
        Cookies.remove(this._getCookieName())
      }
    },
    async gfLogout() {
      let redirect = { name: 'Login' }
      const userData = this.gfGetCurrentUserData()
      if (userData && userData.user_type === 'student') {
        redirect = { name: 'StudentLogin', params: { username: userData.username } }
      }
      this.gfRemoveUserCookies()
      this.$store.dispatch('logoutTimetableCache');
      this.$router.replace(redirect)
    },
    async gfForceLogout() {
      this.$store.dispatch('toggleLoadingPage', true)
      this.$store.dispatch('toggleSnack', {
        message: 'message.loginExpired',
        type: 'error',
      })

      setTimeout(() => {
        this.gfLogout()
      }, 2000)
    },

    // =============================== Setting Related ===============================
    _getSettingCookieName() {
      return `${this.$config.VUE_APP_RUN_MODE}_CMS_SETTING`
    },

    /**
     * { schoolOptions: [], logo: '' }
     */
    gfGetCurrentSettingData() {
      try {
        const data = localStorage.getItem(this._getSettingCookieName())
        if (this.$validate.DataValid(data)) {
          const settingData = JSON.parse(data)
          if (this.$validate.DataValid(settingData)) {
            return settingData
          }
        }
      } catch {}

      return {}
    },

    _getSavedSettingData(key) {
      const data = this.gfGetCurrentSettingData()
      if (this.$validate.DataValid(data[key]) && data[key].expired) {
        const now = new Date().getTime()
        if (data[key].expired > now) {
          return data[key].data
        }
      }
      return null
    },
    gfGetSettingSchoolOptions() {
      const data = this._getSavedSettingData('schoolOptions')
      if (data === undefined || data === null) {
        return null
      }

      return data || []
    },
    gfGetSettingLogo() {
      const data = this._getSavedSettingData('logo')
      if (data === undefined || data === null) {
        return null
      }

      return data || ''
    },

    _updateSavedSettingData(key, data, newExpired = true) {
      const settingData = this.gfGetCurrentSettingData()
      let saved = {
        ...settingData,
      }

      const now = new Date().getTime()
      let newObj = {
        data: data,
        last_updated: now,
        expired: new Date(now + 5 * 60 * 1000).getTime(),
      }
      if (!newExpired && settingData[key] && settingData[key].expired) {
        newObj.expired = settingData[key].expired
      }
      saved[key] = newObj

      localStorage.setItem(this._getSettingCookieName(), JSON.stringify(saved))
    },
    gfUpdateSettingSchoolOptions(data, newExpired = true) {
      this._updateSavedSettingData('schoolOptions', data, newExpired)
    },
    gfUpdateSettingLogo(data, newExpired = true) {
      this._updateSavedSettingData('logo', data, newExpired)
    },
    gfExpiredSettingLogo() {
      const settingData = this.gfGetCurrentSettingData()
      let saved = {
        ...settingData,
      }
      if (saved['logo'] !== undefined) {
        delete saved['logo']
      }
      localStorage.setItem(this._getSettingCookieName(), JSON.stringify(saved))
    },
  },
}
</script>
